import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ApiError } from '../api/client'
import { DEAL_STATUSES, dealStatusTone, dealsApi, type DealList, type DealStatus } from '../api/deals'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Button,
  Callout,
  Checkbox,
  DataTable,
  EmptyState,
  FormGrid,
  LoadingState,
  Page,
  PageHeader,
  Pagination,
  Select,
  StatusPill,
  TextField,
} from '../components/ui'

/**
 * R5-01 (D-01) — elenco delle trattative: preventivi fatti al cliente in attesa di esito. Ognuno vede le proprie, quelle
 * dei subordinati e quelle che ha creato (il confine è il server). Attivo solo con il flag «trattative.scheda».
 */
const PAGE_SIZE = 50

const eur = (n: number | null) =>
  n === null ? '—' : n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })

const day = (iso: string | null) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('it-IT') : '—')

export function DealsPage() {
  const flagOn = useFeatureFlag('trattative.scheda')
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const leadId = Number(params.get('leadId')) || undefined
  const customerId = Number(params.get('customerId')) || undefined

  const [status, setStatus] = useState<DealStatus | ''>('Aperta')
  const [mine, setMine] = useState(false)
  const [expiredOnly, setExpiredOnly] = useState(false)
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<DealList | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [off, setOff] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(await dealsApi.list({
        status, mine, expiredOnly, search: query, page, pageSize: PAGE_SIZE,
        ...(leadId ? { leadId } : {}),
        ...(customerId ? { customerId } : {}),
      }))
      setOff(false)
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) setOff(true)
      else setError(e instanceof Error ? e.message : 'Errore nel caricamento delle trattative.')
    } finally {
      setLoading(false)
    }
  }, [status, mine, expiredOnly, query, page, leadId, customerId])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1) }, 300)
    return () => clearTimeout(t)
  }, [search])

  const newHref = `/deals/new${leadId || customerId
    ? `?${new URLSearchParams({ ...(leadId ? { leadId: String(leadId) } : {}), ...(customerId ? { customerId: String(customerId) } : {}) }).toString()}`
    : ''}`

  if (!flagOn || off) {
    return (
      <Page>
        <PageHeader title="Trattative" icon="handshake" />
        <Callout tone="info" role="note">La scheda Trattativa è spenta: si accende dal flag «trattative.scheda».</Callout>
      </Page>
    )
  }

  const items = data?.items ?? []

  return (
    <Page>
      <PageHeader
        title="Trattative"
        icon="handshake"
        {...(data ? { count: data.total } : {})}
        actions={<Button variant="filled" icon="add" onClick={() => navigate(newHref)}>Nuova trattativa</Button>}
      />
      <div className="d-flex flex-column gap-3">
        <Callout tone="info" role="note">
          La trattativa è il preventivo fatto al cliente in attesa di esito; la bozza è la pratica compilata e non ancora inviata
          alla firma. Quando la trattativa è vinta si crea la pratica in bozza già compilata.
        </Callout>
        {(leadId || customerId) && (
          <Callout tone="info">
            Filtrate per {leadId ? <Link to={`/leads/${leadId}`}>lead n. {leadId}</Link> : <Link to={`/customers/${customerId}`}>cliente n. {customerId}</Link>}
            {' · '}<Link to="/deals">mostra tutte</Link>
          </Callout>
        )}
        <FormGrid columns={4}>
          <div>
            <TextField label="Cerca" placeholder="Titolo o offerta" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div>
            <Select label="Stato" value={status} onChange={e => { setStatus(e.target.value as DealStatus | ''); setPage(1) }}>
              <option value="">Tutti</option>
              {DEAL_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Select>
          </div>
          <div className="d-flex align-items-end">
            <Checkbox id="deals-mine" label="Solo le mie" checked={mine} onChange={e => { setMine(e.target.checked); setPage(1) }} />
          </div>
          <div className="d-flex align-items-end">
            <Checkbox id="deals-expired" label="Solo scadute" checked={expiredOnly}
              onChange={e => { setExpiredOnly(e.target.checked); setPage(1) }} />
          </div>
        </FormGrid>
        {error && <Callout tone="error">{error}</Callout>}

        {loading ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState icon="handshake" title="Nessuna trattativa." />
        ) : (
          <>
            <DataTable>
              <thead>
                <tr>
                  <th>Trattativa</th><th>Cliente / lead</th><th>Importo</th><th>Preventivo del</th><th>Scadenza</th>
                  <th>Probabilità</th><th>Stato</th>
                </tr>
              </thead>
              <tbody>
                {items.map(d => (
                  <tr key={d.id}>
                    <td><Link to={`/deals/${d.id}`}>{d.title}</Link></td>
                    <td>
                      {d.customerId
                        ? <Link to={`/customers/${d.customerId}`}>Cliente n. {d.customerId}</Link>
                        : d.leadId ? <Link to={`/leads/${d.leadId}`}>Lead n. {d.leadId}</Link> : '—'}
                    </td>
                    <td className="text-nowrap">{eur(d.quoteAmount)}</td>
                    <td>{day(d.quoteDate)}</td>
                    <td>{day(d.expiresOn)}</td>
                    <td>{d.probability === null ? '—' : `${d.probability}%`}</td>
                    <td>
                      <StatusPill tone={dealStatusTone(d)}>{d.status === 'Aperta' && d.isExpired ? 'Scaduta' : d.status}</StatusPill>
                      {d.contractId && <> <Link to={`/contracts/${d.contractId}`}>pratica</Link></>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
            <Pagination page={page} pageSize={PAGE_SIZE} total={data?.total ?? 0} onChange={setPage} />
          </>
        )}
      </div>
    </Page>
  )
}
